import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Person,
  Settings,
  Logout,
  LocalBar,
  Archive,
  Star,
  TrendingUp,
  Edit,
  MoreVert,
  ShoppingCart,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { logoutLocal, fetchCurrentUser } from '../../store/slices/authSlice';
import { AppLayout } from '../../components/common/AppLayout';
import { ROUTES, BOTTLE_TYPES_LABELS } from '../../../core/config/constants';
import { getBottleTypeColor } from '../../../core/theme/theme';

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { totalBottles, totalArchivedBottles, bottles } = useAppSelector((state) => state.bottles);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Fetch user data if authenticated but user data is missing
  React.useEffect(() => {
    console.log('🚀 ProfilePage - useEffect triggered');
    console.log('🚀 isAuthenticated:', isAuthenticated);
    console.log('🚀 user:', user);
    
    if (isAuthenticated && !user) {
      console.log('🚀 Appel de fetchCurrentUser...');
      dispatch(fetchCurrentUser()).then((result) => {
        console.log('🚀 fetchCurrentUser terminé, résultat:', result);
        console.log('🚀 payload:', result.payload);
      });
    }
  }, [dispatch, isAuthenticated, user]);

  // Log user data whenever it changes
  React.useEffect(() => {
    console.log('👤 User data updated:', user);
    if (user) {
      console.log('👤 User fields:', {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        mail: user.mail,
        createdAt: user.createdAt,
        allKeys: Object.keys(user)
      });
    }
  }, [user]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutLocal());
    handleMenuClose();
    navigate(ROUTES.LOGIN, { replace: true });
  };


  const handleSettings = () => {
    navigate(ROUTES.SETTINGS);
    handleMenuClose();
  };

  const handleCartClick = () => {
    window.open('https://vinosk.fr/api/payment/shop/tag', '_blank');
  };

  // Calculate statistics
  const averageRating = bottles.length > 0 
    ? (bottles.reduce((sum, bottle) => sum + bottle.rating, 0) / bottles.length).toFixed(1)
    : '0';


  return (
    <AppLayout>
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Box
            component="img"
            src="https://vinosk.fr/images/logo-white.png"
            alt="Vinosk Logo"
            sx={{
              height: 32,
              width: 32,
              mr: 2,
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Profil
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="large"
              color="inherit"
              onClick={handleCartClick}
              sx={{ mr: 1 }}
              title="Panier"
            >
              <ShoppingCart />
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              onClick={handleMenuOpen}
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        {/* Profile Header */}
        {/* <Paper sx={{ p: 3, mb: 3, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 80,
                height: 80,
                fontSize: '2rem',
                mr: 3,
              }}
            >
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Utilisateur'}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {user?.mail || 'Email non disponible'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Membre depuis {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : 'la création du compte'}
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                Debug - User data: {user ? JSON.stringify(user) : 'User is null'}
              </Typography>
            </Box>
          </Box>
        </Paper> */}

        {/* Statistics */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 3,
          }}
        >
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <LocalBar sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {totalBottles}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bouteilles en cave
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Archive sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 600 }}>
                {totalArchivedBottles}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bouteilles dégustées
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Star sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {averageRating}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Note moyenne
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {totalBottles + totalArchivedBottles}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total collection
              </Typography>
            </CardContent>
          </Card>
        </Box>


        {/* Menu Options */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <List>

            <ListItemButton onClick={handleSettings}>
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText
                primary="Paramètres"
                secondary="Configuration de l'application"
              />
            </ListItemButton>

            <Divider />

            <ListItemButton 
              onClick={() => {
                dispatch(logoutLocal());
                navigate(ROUTES.LOGIN, { replace: true });
              }} 
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Logout sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Déconnexion"
                secondary="Se déconnecter de l'application"
              />
            </ListItemButton>
          </List>
        </Paper>
      </Container>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleSettings}>
          <Settings sx={{ mr: 1 }} />
          Paramètres
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <Logout sx={{ mr: 1 }} />
          Déconnexion
        </MenuItem>
      </Menu>
    </AppLayout>
  );
};